import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { blogSettingsService, type BlogSettings } from "@/services/blogSettingsService";
import { toast } from "sonner";
import { isBrowser } from "@/utils/runtime";

const defaultForm: BlogSettings = {
  metaTitle: "",
  metaDescription: "",
  heroTitle: "",
  heroSubtitle: "",
  defaultAuthor: "",
  defaultOgImage: "",
};

const BlogSettings: React.FC = () => {
  const [form, setForm] = useState<BlogSettings>(defaultForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function loadSettings() {
      try {
        setLoading(true);
        const settings = await blogSettingsService.getSettings();
        if (!isMounted) return;
        setForm(settings);
      } catch (error) {
        console.error("Erro ao carregar configurações do blog", error);
        toast.error("Não foi possível carregar as configurações do blog");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadSettings();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleChange = (field: keyof BlogSettings) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      setSaving(true);
      await blogSettingsService.updateSettings(form);
      toast.success("Configurações atualizadas com sucesso");
    } catch (error) {
      console.error("Erro ao atualizar configurações do blog", error);
      toast.error("Não foi possível atualizar as configurações");
    } finally {
      setSaving(false);
    }
  };

  const previewUrl = isBrowser && typeof globalThis.location !== "undefined"
    ? `${globalThis.location.origin}/blog`
    : "https://toctoc.com.br/blog";

  return (
    <div className="space-y-6">
      <Card className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-slate-900">Configurações de SEO</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-4">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="metaTitle">Título (Meta title)</Label>
                <Input
                  id="metaTitle"
                  value={form.metaTitle}
                  onChange={handleChange("metaTitle")}
                  placeholder="Blog Toc Toc - Conteúdo imobiliário"
                />
                <p className="text-xs text-slate-500">Sugestão de até 60 caracteres para resultados do Google.</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="metaDescription">Descrição (Meta description)</Label>
                <Textarea
                  id="metaDescription"
                  value={form.metaDescription}
                  onChange={handleChange("metaDescription")}
                  placeholder="Descubra tendências, ferramentas e boas práticas do mercado imobiliário com a Toc Toc."
                  rows={4}
                />
                <p className="text-xs text-slate-500">Sugestão de até 160 caracteres.</p>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="heroTitle">Título da página do blog</Label>
                  <Input
                    id="heroTitle"
                    value={form.heroTitle}
                    onChange={handleChange("heroTitle")}
                    placeholder="Histórias que inspiram novos lares"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="defaultAuthor">Autor padrão</Label>
                  <Input
                    id="defaultAuthor"
                    value={form.defaultAuthor}
                    onChange={handleChange("defaultAuthor")}
                    placeholder="Equipe Toc Toc"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="heroSubtitle">Subtítulo da página</Label>
                <Textarea
                  id="heroSubtitle"
                  value={form.heroSubtitle}
                  onChange={handleChange("heroSubtitle")}
                  placeholder="Conteúdo exclusivo com dicas, notícias e boas práticas para o seu dia a dia imobiliário."
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="defaultOgImage">Imagem padrão para compartilhamento (URL)</Label>
                <Input
                  id="defaultOgImage"
                  value={form.defaultOgImage}
                  onChange={handleChange("defaultOgImage")}
                  placeholder="https://.../imagem-compartilhamento.png"
                />
                <p className="text-xs text-slate-500">A URL deve apontar para uma imagem 1200x630px disponível publicamente.</p>
              </div>

              <div className="flex justify-end">
                <Button type="submit" disabled={saving} className="bg-[#FF5605] hover:bg-[#e34b00]">
                  {saving ? "Salvando..." : "Salvar configurações"}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>

      <Card className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-slate-900">Pré-visualização</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-3 w-32" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-3/4" />
            </div>
          ) : (
            <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
              <p className="text-xs uppercase text-slate-500">Resultado no Google</p>
              <div className="mt-3 space-y-1">
                <p className="text-lg font-semibold text-blue-700">{form.metaTitle || "Blog Toc Toc"}</p>
                <p className="text-xs text-green-700">{previewUrl}</p>
                <p className="text-sm text-slate-600">{form.metaDescription || "Descubra tendências e novidades da Toc Toc."}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BlogSettings;
